import {
    ClientOnlyFileProviderType,
    FileProviderViewState,
    FileSelectionFilter,
} from 'owa-fileprovider-store';
import { getFileProviders } from 'owa-fileprovider-store/lib/selectors/getFileProviders';
import getSupportedFileSelectionFilters from 'owa-fileprovider-store/lib/utils/getSupportedFileSelectionFilters';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import getAttachmentPolicy from 'owa-session-store/lib/utils/getAttachmentPolicy';
import type { FileProvidersFilter } from '../store/schema/FileProvidersFilter';
import { filterBasedOnFileProvidersFilter } from './filterBasedOnFileProvidersFilter';

export default function getAddedProviders(
    fileSelectionFilter?: FileSelectionFilter,
    fileProvidersFilter?: FileProvidersFilter
): FileProviderViewState[] {
    const addedFileProviders = getFileProviders();
    const hideOneDrive = shouldHideOneDrive();
    const hideGroups = shouldHideGroups();

    const filteredAddedFileProviders = addedFileProviders
        .filter(
            filterForFileProviderSupportingSelectionFilter(
                fileSelectionFilter,
                hideOneDrive,
                hideGroups
            )
        )
        .filter(filterBasedOnFileProvidersFilter(fileProvidersFilter));
    return sortFileProvidersViewState(filteredAddedFileProviders);
}

// If the user is using delegate email we should not show Groups option
export function shouldHideGroups() {
    const attachmentPolicy = getAttachmentPolicy();
    if (!!attachmentPolicy && !attachmentPolicy.GroupsOneDriveDataProviderAvailable) {
        return true;
    }

    const configuration = getUserConfiguration();
    return configuration.SessionSettings?.IsExplicitLogon;
}

export function shouldHideOneDrive() {
    const attachmentPolicy = getAttachmentPolicy();
    return (
        !!attachmentPolicy &&
        !attachmentPolicy.AttachmentDataProviderAvailable &&
        attachmentPolicy.GroupsOneDriveDataProviderAvailable
    );
}

function filterForFileProviderSupportingSelectionFilter(
    fileSelectionFilter: FileSelectionFilter,
    hideOneDrive: boolean,
    hideGroups: boolean
): (viewState: FileProviderViewState) => boolean {
    return (viewState: FileProviderViewState) => {
        // If the user has a license for groups, but not for OneDrive, we filter out the OneDrive provider.
        // We are relying on server sending this OneDrive provider without a name if it should not show up
        if (hideOneDrive && !viewState.name) {
            return false;
        }
        if (hideGroups && viewState.viewType === ClientOnlyFileProviderType.Groups) {
            return false;
        }

        const isNoneFilter =
            fileSelectionFilter === FileSelectionFilter.None || !fileSelectionFilter;

        // Return true if it is a None filter, if not then check if the file provider supports that filter
        return (
            isNoneFilter ||
            getSupportedFileSelectionFilters(viewState.type).indexOf(fileSelectionFilter) !== -1
        );
    };
}

export function sortFileProvidersViewState(providers: FileProviderViewState[]) {
    const filteredProviders = providers
        .filter(p => p.type !== AttachmentDataProviderType.Mailbox)
        .filter(p => p.type !== AttachmentDataProviderType.MailMessage)
        .filter(p => !p.associatedWithPrimaryProvider);
    const primaryProviders = filteredProviders.filter(p => p.isPrimaryProvider);
    const followingPrimaryProviders = providers.filter(p => p.associatedWithPrimaryProvider);
    const alphabeticallySortedSecondaryProviders = filteredProviders
        .filter(p => !p.isPrimaryProvider)
        .sort((a, b) => a.name.localeCompare(b.name));

    // The mailbox provider is placed second to the end
    const mailboxProvider = providers.filter(p => p.type === AttachmentDataProviderType.Mailbox);

    // Mail Messages provider is placed at the end
    const mailMessageProvider = providers.filter(
        p => p.type === AttachmentDataProviderType.MailMessage
    );

    return primaryProviders
        .concat(followingPrimaryProviders)
        .concat(alphabeticallySortedSecondaryProviders)
        .concat(mailboxProvider)
        .concat(mailMessageProvider);
}
